import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useRecruiter } from "../context/RecruiterContext.tsx";
import { MessageCircle } from 'lucide-react';

interface NavbarProps {
    isLoggedIn: boolean;
    setIsLoggedIn: (isLoggedIn: boolean) => void;
    mailNumber: number;
}

interface NavLinkProps {
    to: string;
    children: React.ReactNode;
    className?: string;
    hasNotification?: boolean;
    notificationCount?: number;
}

function NavLink({ to, children, className = "", hasNotification, notificationCount = 0 }: NavLinkProps) {
    const baseClasses = "relative group transition-colors duration-200 flex items-center";
    const desktopClasses = "text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium";
    const mobileClasses = "text-gray-300 hover:text-white block px-3 py-2 rounded-md text-base font-medium";

    return (
        <Link
            to={to}
            className={`${baseClasses} ${window.innerWidth >= 768 ? desktopClasses : mobileClasses} ${className}`}
        >
            {children}
            {hasNotification && notificationCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                    {notificationCount}
                </span>
            )}
            <span className="absolute inset-x-0 bottom-0 h-0.5 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left"></span>
        </Link>
    );
}

function Navbar({ isLoggedIn, setIsLoggedIn, mailNumber }: NavbarProps) {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const navigate = useNavigate();
    const recruiter = useRecruiter();
    const { isLoggedIn: recruiterLoggedIn, isRecruiter, unread } = useRecruiter();

    const onLogout = async () => {
        try {
            const response = await fetch('/auth/logout/google', {
                method: 'GET',
                credentials: 'include',
            });

            if (response.ok) {
                setIsLoggedIn(false);
                navigate('/login');
            }
        } catch (error) {
            console.error('Error during logout:', error);
        }
    };

    const navigationLinks = (
        <>
            <NavLink to="/comparertoj">Compare</NavLink>
            <NavLink to="/createJd">Post a Job</NavLink>
            {isRecruiter && (
                <NavLink to="/your-job-to-resumes-path">
                    Compare JD to Resumes
                </NavLink>
            )}
            {isLoggedIn && !recruiter.isRecruiter ? (
                <>
                    <NavLink to="/profile">Profile</NavLink>
                    <NavLink
                        to="/chat"
                        hasNotification={true}
                        notificationCount={unread}
                        className="flex items-center space-x-1"
                    >
                        <MessageCircle className="w-4 h-4" />
                        <span>Chat</span>
                    </NavLink>
                </>
            ) : (
                <NavLink to="/dashboard">Dashboard</NavLink>
            )}
        </>
    );

    return (
        <header className="sticky top-0 z-50">
            <nav className="bg-gray-800 text-white shadow-lg backdrop-blur-sm bg-opacity-95" role="navigation" aria-label="Main navigation">
                <div className="container mx-auto px-4">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center">
                            <Link to="/" className="flex items-center space-x-3 group">
                                <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-blue-600">
                                    CVSeeyou
                                </span>
                            </Link>

                            <div className="hidden md:flex items-center ml-10 space-x-4">
                                {navigationLinks}
                            </div>
                        </div>

                        <div className="flex items-center space-x-4">
                            {isLoggedIn ? (
                                <button
                                    onClick={onLogout}
                                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 hover:scale-105"
                                >
                                    Logout
                                </button>
                            ) : (
                                <Link
                                    to="/login"
                                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 hover:scale-105"
                                >
                                    Login
                                </Link>
                            )}

                            <button
                                type="button"
                                className="md:hidden p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white transition-colors duration-200"
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            >
                                <svg
                                    className={`h-6 w-6 transition-transform duration-200 ${isMobileMenuOpen ? 'rotate-90' : ''}`}
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M4 6h16M4 12h16M4 18h16"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {isMobileMenuOpen && (
                    <div className="md:hidden animate-fadeIn">
                        <div className="px-2 pt-2 pb-3 space-y-1">
                            {navigationLinks}
                        </div>
                    </div>
                )}
            </nav>
        </header>
    );
}



function Footer() {
    const footerLinks = [
        { href: "/contact", text: "Contact" },
        { href: "/resume-to-jobs-path", text: "Resume To Job" },
        { href: "/privacy", text: "Privacy Policy" },
        { href: "/terms", text: "Terms of Service" }
    ];

    return (
        <footer className="bg-gray-900 text-white py-12 mt-auto">
            <div className="container mx-auto px-6">
                <div className="border-t border-gray-800 mt-8 pt-8">
                    <div className="flex flex-col items-center justify-center space-y-4">
                        <p className="text-gray-400">&copy; 2024 CVSeeyou. All rights reserved.</p>
                        <div className="flex flex-wrap justify-center gap-4">
                            {footerLinks.map((link) => (
                                <a
                                    key={link.href}
                                    href={link.href}
                                    className="text-gray-400 hover:text-white transition-colors duration-200 relative group"
                                >
                                    {link.text}
                                    <span className="absolute inset-x-0 bottom-0 h-0.5 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left"></span>
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}

interface LayoutProps {
    isLoggedIn: boolean;
    setIsLoggedIn: (isLoggedIn: boolean) => void;
    children: React.ReactNode;
}

function Layout({ isLoggedIn, setIsLoggedIn, children }: LayoutProps) {
    const recruiter = useRecruiter();

    return (

        <div className="min-h-screen flex flex-col bg-gray-50">

            <Navbar
                isLoggedIn={isLoggedIn}
                setIsLoggedIn={setIsLoggedIn}
                mailNumber={recruiter.unread}
            />
            <main className="flex-grow container mx-auto px-4 py-8">
                {children}
            </main>
            <Footer />
        </div>
    );
}

export { Layout, Navbar, Footer };